/** @format */

import React, { Suspense, useState, useRef, useEffect } from "react";
import { motion, useAnimation, AnimatePresence } from "framer-motion";
import {
  Car,
  User,
  MapPin,
  FileText,
  Camera,
  CheckCircle,
  Save,
  AlertTriangle,
  X,
  Eye,
  Search,
  Loader as LoaderIcon,
  Calendar,
  List,
  Wrench,
  PlusCircle,
  ShieldCheck,
  Phone,
  Mail,
  Trash2,
  Upload,
  RotateCcw,
  SwitchCamera,
} from "lucide-react";
import SignatureCanvas from "react-signature-canvas";
import { supabase, verifyRequiredBuckets } from "@lib/supabaseClient";
import { useNavigate, Link } from "react-router-dom";

// Composant Loader personnalisé
const CustomLoader = () => {
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className='fixed inset-0 z-50 flex items-center justify-center bg-slate-900/80 backdrop-blur-sm'>
        <div className='text-center'>
          <motion.div
            animate={{
              rotate: 360,
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "linear",
            }}
            className='inline-block'>
            <Car className='w-16 h-16 text-blue-400' />
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className='mt-4 text-lg text-white font-medium'>
            Chargement en cours...
          </motion.p>
          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            className='mt-4 h-1 w-48 mx-auto bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500 rounded-full origin-left'
          />
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

// ============ COMPOSANT CAMERA AVEC APERÇU ============
const CameraModal = ({ onCapture, onClose }) => {
  const videoRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [facingMode, setFacingMode] = useState("environment");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const startCamera = async (facing = "environment") => {
    setIsLoading(true);
    setError(null);

    // Arrêter le stream précédent s'il existe
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }

    try {
      const constraints = {
        video: {
          facingMode: { ideal: facing },
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        }
      };

      const newStream = await navigator.mediaDevices.getUserMedia(constraints);
      setStream(newStream);
      
      if (videoRef.current) {
        videoRef.current.srcObject = newStream;
        await videoRef.current.play();
      }
    } catch (err) {
      console.error("Erreur caméra:", err);
      
      // Essayer sans contrainte de facing mode
      try {
        const fallbackStream = await navigator.mediaDevices.getUserMedia({
          video: { width: { ideal: 1920 }, height: { ideal: 1080 } }
        });
        setStream(fallbackStream);
        
        if (videoRef.current) {
          videoRef.current.srcObject = fallbackStream;
          await videoRef.current.play();
        }
      } catch (fallbackErr) {
        setError("Impossible d'accéder à la caméra. Vérifiez les permissions.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    startCamera(facingMode);

    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  const switchCamera = () => {
    const newFacing = facingMode === "environment" ? "user" : "environment";
    setFacingMode(newFacing);
    startCamera(newFacing);
  };

  const capturePhoto = () => {
    if (!videoRef.current) return;

    const canvas = document.createElement("canvas");
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(videoRef.current, 0, 0);

    canvas.toBlob((blob) => {
      const file = new File([blob], `photo_${Date.now()}.jpg`, { type: "image/jpeg" });
      const fileUrl = URL.createObjectURL(blob);
      
      // Arrêter le stream
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
      
      onCapture(file, fileUrl);
    }, "image/jpeg", 0.9);
  };

  const handleClose = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
    onClose();
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black flex flex-col"
    >
      {/* Header */}
      <div className="bg-slate-900 px-4 py-3 flex items-center justify-between">
        <h3 className="text-white font-semibold flex items-center gap-2">
          <Camera className="w-5 h-5" />
          Prendre une photo
        </h3>
        <button
          onClick={handleClose}
          className="text-white p-2 hover:bg-slate-700 rounded-full"
        >
          <X className="w-6 h-6" />
        </button>
      </div>

      {/* Zone vidéo */}
      <div className="flex-1 relative flex items-center justify-center bg-black">
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/50 z-10">
            <LoaderIcon className="w-12 h-12 text-white animate-spin" />
          </div>
        )}

        {error ? (
          <div className="text-center p-6">
            <AlertTriangle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <p className="text-red-400 text-lg">{error}</p>
            <button
              onClick={() => startCamera(facingMode)}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg flex items-center gap-2 mx-auto"
            >
              <RotateCcw className="w-5 h-5" />
              Réessayer
            </button>
          </div>
        ) : (
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="max-h-full max-w-full object-contain"
          />
        )}

        {/* Guide de cadrage */}
        {!error && !isLoading && (
          <div className="absolute inset-8 border-2 border-white/30 rounded-lg pointer-events-none">
            <div className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-white rounded-tl-lg" />
            <div className="absolute top-0 right-0 w-8 h-8 border-t-4 border-r-4 border-white rounded-tr-lg" />
            <div className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 border-white rounded-bl-lg" />
            <div className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-white rounded-br-lg" />
          </div>
        )}
      </div>

      {/* Contrôles */}
      <div className="bg-slate-900 px-4 py-6 flex items-center justify-center gap-8">
        {/* Bouton switch caméra */}
        <button
          onClick={switchCamera}
          disabled={isLoading || error}
          className="p-4 bg-slate-700 hover:bg-slate-600 disabled:bg-slate-800 disabled:text-slate-500 text-white rounded-full transition-colors"
        >
          <SwitchCamera className="w-6 h-6" />
        </button>

        {/* Bouton capture */}
        <button
          onClick={capturePhoto}
          disabled={isLoading || error}
          className="p-6 bg-white hover:bg-gray-200 disabled:bg-gray-500 rounded-full transition-colors"
        >
          <div className="w-12 h-12 bg-red-500 rounded-full" />
        </button>

        {/* Placeholder pour symétrie */}
        <div className="w-14 h-14" />
      </div>

      {/* Instructions */}
      <div className="bg-slate-800 px-4 py-3 text-center">
        <p className="text-gray-300 text-sm">
          Cadrez la carte grise dans le rectangle et appuyez sur le bouton rouge
        </p>
      </div>
    </motion.div>
  );
};

// ============ COMPOSANT DRAG & DROP ============
const FileDropZone = ({ onFileSelect, accept, children, className }) => {
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef(null);

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.type.startsWith("image/") || file.type === "application/pdf") {
        const fileUrl = URL.createObjectURL(file);
        onFileSelect(file, fileUrl);
      } else {
        alert("Veuillez déposer une image ou un PDF");
      }
    }
  };

  const handleClick = () => {
    inputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const fileUrl = URL.createObjectURL(file);
      onFileSelect(file, fileUrl);
    }
  };

  return (
    <div
      onClick={handleClick}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={`
        relative cursor-pointer transition-all duration-200
        ${isDragging ? "ring-4 ring-blue-500 bg-blue-500/20" : ""}
        ${className}
      `}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={handleFileChange}
        className="hidden"
      />
      
      {isDragging && (
        <div className="absolute inset-0 bg-blue-500/30 rounded-lg flex items-center justify-center z-10">
          <div className="text-center text-white">
            <Upload className="w-12 h-12 mx-auto mb-2" />
            <p className="font-semibold">Déposez le fichier ici</p>
          </div>
        </div>
      )}
      
      {children}
    </div>
  );
};

const ClientForm = () => {
  const navigate = useNavigate();
  const controls = useAnimation();
  const logoControls = useAnimation();
  const formRef = useRef(null);
  const clientSignatureRef = useRef(null);
  const trainerSignatureRef = useRef(null);
  const managerSignatureRef = useRef(null);
  const pdfRef = useRef(null);

  const [previewMode, setPreviewMode] = useState(false);
  const [canvasSize, setCanvasSize] = useState({ width: 400, height: 200 });
  const [showClientsList, setShowClientsList] = useState(false);
  const [allClients, setAllClients] = useState([]);
  const [isAdmin, setIsAdmin] = useState(true);
  const [missingBuckets, setMissingBuckets] = useState([]);
  const [currentClientId, setCurrentClientId] = useState(null);
  const [searchPlate, setSearchPlate] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [interventions, setInterventions] = useState([]);
  const [nextInterventionDate, setNextInterventionDate] = useState("");
  const [interventionType, setInterventionType] = useState("");
  const [insuranceConfirmation, setInsuranceConfirmation] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showCamera, setShowCamera] = useState(false);

  const [trainers, setTrainers] = useState([
    { id: "default-1", name: "Jean DUPONT" },
    { id: "default-2", name: "Guillaume BEAUPLE" },
    { id: "default-3", name: "Sébastien SERGENT" },
    { id: "default-4", name: "Pierre RICHARD" },
    { id: "default-5", name: "Marie LAURENT" },
  ]);

  const [formData, setFormData] = useState({
    immatriculation: "",
    firstName: "",
    lastName: "",
    address: "",
    postalCode: "",
    city: "",
    phone: "",
    email: "",
    trainerName: "",
    technicalControlDate: "",
    termsAccepted: false,
    insuranceResponsibility: false,
  });

  const [files, setFiles] = useState({ registration: null });
  const [fileUrls, setFileUrls] = useState({ registration: null });
  const [signatures, setSignatures] = useState({
    client: null,
    trainer: null,
    manager: null,
  });

  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  // ============ FONCTION POUR CHARGER LES SIGNATURES ============
  const loadSignaturesToCanvas = (clientData) => {
    console.log("🔄 Chargement des signatures sur les canvas:", {
      client: clientData.client_signature ? "✓" : "✗",
      trainer: clientData.trainer_signature ? "✓" : "✗",
      manager: clientData.manager_signature ? "✓" : "✗",
    });

    setTimeout(() => {
      if (clientData.client_signature && clientSignatureRef.current) {
        clientSignatureRef.current.clear();
        clientSignatureRef.current.fromDataURL(clientData.client_signature);
        console.log("✅ Signature client chargée");
      }

      if (clientData.trainer_signature && trainerSignatureRef.current) {
        trainerSignatureRef.current.clear();
        trainerSignatureRef.current.fromDataURL(clientData.trainer_signature);
        console.log("✅ Signature trainer chargée");
      }

      if (clientData.manager_signature && managerSignatureRef.current) {
        managerSignatureRef.current.clear();
        managerSignatureRef.current.fromDataURL(clientData.manager_signature);
        console.log("✅ Signature manager chargée");
      }
    }, 200);

    setSignatures({
      client: clientData.client_signature || null,
      trainer: clientData.trainer_signature || null,
      manager: clientData.manager_signature || null,
    });
  };

  // ============ FONCTION POUR RECHARGER LES CLIENTS ============
  const fetchAllClients = async () => {
    try {
      const { data, error } = await supabase
        .from("clients")
        .select("*")
        .order("created_at", { ascending: false });

      if (!error && data) {
        setAllClients(data);
        console.log("✅ Liste des clients rechargée:", data.length, "clients");
      }
    } catch (error) {
      console.error("❌ Erreur lors du rechargement des clients:", error);
    }
  };

  // ============ FONCTION POUR SUPPRIMER UN CLIENT ============
  const handleDeleteClient = async (clientId, clientName) => {
    if (
      !confirm(
        `Êtes-vous sûr de vouloir supprimer le client "${clientName}" ?\n\nCette action est irréversible et supprimera également tous les documents associés.`
      )
    ) {
      return;
    }

    try {
      const { data: clientData, error: fetchError } = await supabase
        .from("clients")
        .select("registration_file, contract_file")
        .eq("id", clientId)
        .single();

      if (fetchError) throw fetchError;

      const filesToDelete = [];
      if (clientData.registration_file) {
        filesToDelete.push(clientData.registration_file);
      }
      if (clientData.contract_file) {
        filesToDelete.push(clientData.contract_file);
      }

      if (filesToDelete.length > 0) {
        const { error: storageError } = await supabase.storage
          .from("client-documents")
          .remove(filesToDelete);

        if (storageError) {
          console.warn("⚠️ Erreur lors de la suppression des fichiers:", storageError);
        }
      }

      const { error: deleteError } = await supabase
        .from("clients")
        .delete()
        .eq("id", clientId);

      if (deleteError) throw deleteError;

      alert("✅ Client supprimé avec succès");
      await fetchAllClients();
    } catch (error) {
      console.error("❌ Erreur lors de la suppression:", error);
      alert("Erreur lors de la suppression du client. Veuillez réessayer.");
    }
  };

  // ============ USEEFFECTS ============
  useEffect(() => {
    const initializeComponent = async () => {
      setIsLoading(true);
      try {
        const { success, missingBuckets: missing } = await verifyRequiredBuckets();
        setMissingBuckets(missing);

        if (isAdmin) {
          const { data, error } = await supabase
            .from("clients")
            .select("*")
            .order("created_at", { ascending: false });

          if (!error && data) {
            setAllClients(data);
          }
        }

        const { data: formatorsData, error: formatorsError } = await supabase
          .from("formateurs")
          .select("id, nom, prenom")
          .order("nom");

        if (!formatorsError && formatorsData && formatorsData.length > 0) {
          const formattedTrainers = formatorsData.map((f) => ({
            id: f.id,
            name: `${f.prenom} ${f.nom}`,
          }));

          const hasBeauple = formattedTrainers.some(
            (f) =>
              f.name.toLowerCase().includes("beauple") ||
              f.name.toLowerCase().includes("guillaume")
          );

          const hasSergent = formattedTrainers.some(
            (f) =>
              f.name.toLowerCase().includes("sergent") ||
              f.name.toLowerCase().includes("sébastien")
          );

          if (!hasBeauple) {
            formattedTrainers.push({
              id: `temp-beauple-${Date.now()}`,
              name: "Guillaume BEAUPLE",
            });
          }

          if (!hasSergent) {
            formattedTrainers.push({
              id: `temp-sergent-${Date.now()}`,
              name: "Sébastien SERGENT",
            });
          }

          if (formattedTrainers.length < 5) {
            const defaultFormateurs = [
              { name: "Jean DUPONT" },
              { name: "Pierre RICHARD" },
              { name: "Marie LAURENT" },
            ];

            for (const defaultFormateur of defaultFormateurs) {
              if (
                !formattedTrainers.some((t) =>
                  t.name.includes(defaultFormateur.name.split(" ")[0])
                )
              ) {
                formattedTrainers.push({
                  id: `temp-${defaultFormateur.name.replace(/\s+/g, "-").toLowerCase()}-${Date.now()}`,
                  name: defaultFormateur.name,
                });
              }
            }
          }

          setTrainers(formattedTrainers);
        }
      } catch (error) {
        console.error("Erreur lors de l'initialisation:", error);
      } finally {
        setTimeout(() => setIsLoading(false), 1000);
      }
    };

    initializeComponent();
  }, []);

  useEffect(() => {
    const updateCanvasSize = () => {
      const width = window.innerWidth < 768 ? window.innerWidth - 80 : 400;
      setCanvasSize({ width, height: 200 });
    };

    updateCanvasSize();
    window.addEventListener("resize", updateCanvasSize);
    return () => window.removeEventListener("resize", updateCanvasSize);
  }, []);

  useEffect(() => {
    const logoAnimation = async () => {
      await logoControls.start({
        scale: [0.8, 1.2, 1],
        rotate: [0, 15, -15, 0],
        opacity: [0, 1],
        transition: { duration: 2 },
      });

      logoControls.start({
        y: [0, -15, 0],
        scale: [1, 1.1, 1],
        transition: {
          repeat: Infinity,
          duration: 4,
          ease: "easeInOut",
        },
      });
    };

    logoAnimation();
  }, [logoControls]);

  // ============ FONCTIONS UTILITAIRES ============
  const loadInterventions = async (immatriculation) => {
    try {
      const { data, error } = await supabase
        .from("interventions")
        .select("*")
        .eq("immatriculation", immatriculation)
        .order("completion_date", { ascending: true });

      if (error) {
        console.error("Erreur lors du chargement des interventions:", error);
        return;
      }

      if (data) {
        setInterventions(data);
        const futureInterventions = data
          .filter((i) => new Date(i.completion_date) >= new Date())
          .sort((a, b) => new Date(a.completion_date) - new Date(b.completion_date));

        if (futureInterventions.length > 0) {
          setNextInterventionDate(futureInterventions[0].completion_date);
          setInterventionType(
            futureInterventions[0].intervention_type === "mechanical"
              ? "Mécanique"
              : "Carrosserie"
          );
        }
      }
    } catch (error) {
      console.error("Erreur lors du chargement des interventions:", error);
    }
  };

  const searchClientByPlate = async (e) => {
    e.preventDefault();

    if (!searchPlate.trim()) {
      setSearchError("Veuillez saisir une immatriculation");
      return;
    }

    setIsSearching(true);
    setSearchError("");

    try {
      const { data, error } = await supabase
        .from("clients")
        .select("*")
        .eq("immatriculation", searchPlate.trim())
        .single();

      if (error) {
        if (error.code === "PGRST116") {
          setSearchError("Aucun client trouvé avec cette immatriculation");
        } else {
          setSearchError("Une erreur est survenue lors de la recherche");
          console.error("Erreur Supabase:", error);
        }
        return;
      }

      if (data) {
        console.log("📥 Client trouvé:", data);
        setCurrentClientId(data.id);
        setFormData({
          immatriculation: data.immatriculation,
          firstName: data.first_name,
          lastName: data.last_name,
          address: data.address,
          postalCode: data.postal_code,
          city: data.city,
          phone: data.phone || "",
          email: data.email || "",
          trainerName: data.trainer_name || "",
          technicalControlDate: data.technical_control_date || "",
          termsAccepted: true,
          insuranceResponsibility: data.has_insurance || false,
        });

        if (data.registration_file) {
          const { data: registrationData, error: registrationError } =
            await supabase.storage
              .from("client-documents")
              .createSignedUrl(data.registration_file, 3600);

          if (!registrationError && registrationData) {
            setFileUrls({
              ...fileUrls,
              registration: registrationData.signedUrl,
            });
          }
        }

        loadSignaturesToCanvas(data);
        setInsuranceConfirmation(data.has_insurance || false);
        await loadInterventions(data.immatriculation);
      }
    } catch (error) {
      console.error("Erreur lors de la recherche:", error);
      setSearchError("Une erreur est survenue lors de la recherche");
    } finally {
      setIsSearching(false);
    }
  };

  const loadClientById = async (clientId) => {
    try {
      setIsSearching(true);

      const { data, error } = await supabase
        .from("clients")
        .select("*")
        .eq("id", clientId)
        .single();

      if (error) {
        console.error("Erreur lors du chargement du client:", error);
        return;
      }

      if (data) {
        console.log("📥 Client chargé:", data);
        setCurrentClientId(data.id);
        setFormData({
          immatriculation: data.immatriculation,
          firstName: data.first_name,
          lastName: data.last_name,
          address: data.address,
          postalCode: data.postal_code,
          city: data.city,
          phone: data.phone || "",
          email: data.email || "",
          trainerName: data.trainer_name || "",
          technicalControlDate: data.technical_control_date || "",
          termsAccepted: true,
          insuranceResponsibility: data.has_insurance || false,
        });

        if (data.registration_file) {
          const { data: registrationData, error: registrationError } =
            await supabase.storage
              .from("client-documents")
              .createSignedUrl(data.registration_file, 3600);

          if (!registrationError && registrationData) {
            setFileUrls({
              ...fileUrls,
              registration: registrationData.signedUrl,
            });
          }
        }

        loadSignaturesToCanvas(data);
        setInsuranceConfirmation(data.has_insurance || false);
        await loadInterventions(data.immatriculation);
        setShowClientsList(false);
      }
    } catch (error) {
      console.error("Erreur lors du chargement du client:", error);
    } finally {
      setIsSearching(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });

    if (name === "insuranceResponsibility") {
      setInsuranceConfirmation(checked);
    }

    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null,
      });
    }
  };

  // ============ NOUVELLE FONCTION POUR GERER LES FICHIERS ============
  const handleFileSelect = (file, fileUrl) => {
    setFiles({ ...files, registration: file });
    setFileUrls({ ...fileUrls, registration: fileUrl });
    
    if (errors.registration) {
      setErrors({ ...errors, registration: null });
    }
  };

  // ============ FONCTION POUR LA CAPTURE CAMERA ============
  const handleCameraCapture = (file, fileUrl) => {
    setFiles({ ...files, registration: file });
    setFileUrls({ ...fileUrls, registration: fileUrl });
    setShowCamera(false);
    
    if (errors.registration) {
      setErrors({ ...errors, registration: null });
    }
  };

  const clearSignature = (sigRef, signatureType) => {
    console.log(`🗑️ Effacement signature ${signatureType}`);
    if (sigRef && sigRef.current) {
      sigRef.current.clear();
    }
    setSignatures((prev) => ({ ...prev, [signatureType]: null }));
  };

  const getSignatureData = (sigRef, signatureType) => {
    console.log(`🔍 Récupération signature ${signatureType}`);

    if (sigRef && sigRef.current && !sigRef.current.isEmpty()) {
      const data = sigRef.current.toDataURL();
      console.log(`✅ Signature ${signatureType} depuis canvas, longueur: ${data.length}`);
      return data;
    }

    if (signatures[signatureType]) {
      console.log(`✅ Signature ${signatureType} depuis l'état, longueur: ${signatures[signatureType].length}`);
      return signatures[signatureType];
    }

    console.log(`❌ Pas de signature ${signatureType}`);
    return null;
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.immatriculation.trim())
      newErrors.immatriculation = "L'immatriculation est requise";
    if (!formData.firstName.trim())
      newErrors.firstName = "Le prénom est requis";
    if (!formData.lastName.trim()) newErrors.lastName = "Le nom est requis";
    if (!formData.address.trim()) newErrors.address = "L'adresse est requise";
    if (!formData.postalCode.trim())
      newErrors.postalCode = "Le code postal est requis";
    if (!formData.city.trim()) newErrors.city = "La ville est requise";

    if (!formData.phone.trim()) {
      newErrors.phone = "Le téléphone est requis";
    } else if (!/^[0-9]{10}$/.test(formData.phone.replace(/\s/g, ""))) {
      newErrors.phone = "Le téléphone doit contenir 10 chiffres";
    }

    if (!formData.email.trim()) {
      newErrors.email = "L'email est requis";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "L'email n'est pas valide";
    }

    if (!formData.trainerName.trim())
      newErrors.trainerName = "Le formateur est requis";

    if (!formData.insuranceResponsibility)
      newErrors.insuranceResponsibility =
        "Vous devez confirmer l'attestation d'assurance";

    if (!files.registration && !fileUrls.registration)
      newErrors.registration = "La carte grise est requise";

    const clientSig = getSignatureData(clientSignatureRef, "client");
    const trainerSig = getSignatureData(trainerSignatureRef, "trainer");
    const managerSig = getSignatureData(managerSignatureRef, "manager");

    if (!clientSig)
      newErrors.clientSignature = "La signature du client est requise";
    if (!trainerSig)
      newErrors.trainerSignature = "La signature du formateur est requise";
    if (!managerSig)
      newErrors.managerSignature = "La signature du responsable est requise";

    if (!formData.termsAccepted)
      newErrors.termsAccepted = "Vous devez accepter les mentions légales";

    if (missingBuckets.length > 0) {
      newErrors.buckets = `Configuration incomplète: ${missingBuckets.join(", ")} non disponibles`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const generatePdf = async () => {
    if (pdfRef.current) {
      const html2canvas = (await import("html2canvas")).default;
      const { jsPDF } = await import("jspdf");

      const images = pdfRef.current.querySelectorAll("img");
      await Promise.all(
        Array.from(images).map((img) => {
          if (img.complete) return Promise.resolve();
          return new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = reject;
          });
        })
      );

      await new Promise((resolve) => setTimeout(resolve, 500));

      const canvas = await html2canvas(pdfRef.current, {
        scale: 2,
        useCORS: true,
        logging: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const imgWidth = 210;
      const pageHeight = 295;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      return pdf;
    }
  };

  const saveToSupabase = async (pdfFile) => {
    try {
      setSaving(true);

      if (missingBuckets.length > 0) {
        throw new Error(
          `Configuration incomplète: les buckets suivants sont manquants: ${missingBuckets.join(", ")}`
        );
      }

      let clientSignature = getSignatureData(clientSignatureRef, "client");
      let trainerSignature = getSignatureData(trainerSignatureRef, "trainer");
      let managerSignature = getSignatureData(managerSignatureRef, "manager");

      console.log("📝 Signatures à sauvegarder:", {
        client: clientSignature ? `Longueur: ${clientSignature.length}` : "MANQUANTE",
        trainer: trainerSignature ? `Longueur: ${trainerSignature.length}` : "MANQUANTE",
        manager: managerSignature ? `Longueur: ${managerSignature.length}` : "MANQUANTE",
      });

      if (!clientSignature || !trainerSignature || !managerSignature) {
        throw new Error("Toutes les signatures sont requises");
      }

      const { data: existingClient, error: existingClientError } =
        await supabase
          .from("clients")
          .select("id, registration_file, contract_file")
          .eq("immatriculation", formData.immatriculation)
          .maybeSingle();

      const timestamp = Date.now();
      const registrationFileName = `${formData.immatriculation}_registration_${timestamp}.${files.registration ? files.registration.name.split(".").pop() : "jpg"}`;
      const pdfFileName = `${formData.immatriculation}_contrat_${timestamp}.pdf`;

      if (files.registration) {
        if (existingClient?.registration_file) {
          await supabase.storage
            .from("client-documents")
            .remove([existingClient.registration_file]);
        }

        const { error: registrationError } = await supabase.storage
          .from("client-documents")
          .upload(registrationFileName, files.registration, { upsert: true });

        if (registrationError) throw registrationError;
      }

      const pdfBlob = pdfFile.output("blob");

      if (existingClient?.contract_file) {
        await supabase.storage
          .from("client-documents")
          .remove([existingClient.contract_file]);
      }

      const { error: pdfError } = await supabase.storage
        .from("client-documents")
        .upload(pdfFileName, pdfBlob, { upsert: true });

      if (pdfError) throw pdfError;

      const clientData = {
        immatriculation: formData.immatriculation,
        first_name: formData.firstName,
        last_name: formData.lastName,
        address: formData.address,
        postal_code: formData.postalCode,
        city: formData.city,
        phone: formData.phone,
        email: formData.email,
        trainer_name: formData.trainerName,
        technical_control_date: formData.technicalControlDate || null,
        has_insurance: formData.insuranceResponsibility,
        registration_file:
          files.registration
            ? registrationFileName
            : existingClient?.registration_file || null,
        contract_file: pdfFileName,
        client_signature: clientSignature,
        trainer_signature: trainerSignature,
        manager_signature: managerSignature,
        updated_at: new Date().toISOString(),
      };

      if (existingClient) {
        const { error: updateError } = await supabase
          .from("clients")
          .update(clientData)
          .eq("id", existingClient.id);

        if (updateError) {
          console.error("❌ Erreur mise à jour:", updateError);
          throw updateError;
        }
        console.log("✅ Client mis à jour avec succès");
      } else {
        clientData.created_at = new Date().toISOString();

        const { error: insertError } = await supabase
          .from("clients")
          .insert([clientData]);

        if (insertError) {
          console.error("❌ Erreur insertion:", insertError);
          throw insertError;
        }
        console.log("✅ Client créé avec succès");
      }

      setSuccess(true);

      const { data: pdfUrlData } = await supabase.storage
        .from("client-documents")
        .createSignedUrl(pdfFileName, 3600);

      if (pdfUrlData?.signedUrl) {
        window.open(pdfUrlData.signedUrl, "_blank");
      }

      if (isAdmin) {
        await fetchAllClients();
      }

      setTimeout(() => {
        resetForm();
        setSuccess(false);
      }, 2000);

      return true;
    } catch (error) {
      console.error("❌ Erreur lors de l'enregistrement:", error);
      setErrors({
        submit:
          error.message ||
          "Une erreur est survenue lors de l'enregistrement. Veuillez réessayer.",
      });
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      controls.start({
        x: [0, -10, 10, -10, 0],
        transition: { duration: 0.5 },
      });

      const firstErrorKey = Object.keys(errors)[0];
      const firstErrorElement =
        document.querySelector(`[name="${firstErrorKey}"]`) ||
        document.querySelector(`[data-error="${firstErrorKey}"]`);
      if (firstErrorElement) {
        firstErrorElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }

      return;
    }

    const clientSig = getSignatureData(clientSignatureRef, "client");
    const trainerSig = getSignatureData(trainerSignatureRef, "trainer");
    const managerSig = getSignatureData(managerSignatureRef, "manager");

    setSignatures({
      client: clientSig,
      trainer: trainerSig,
      manager: managerSig,
    });

    setPreviewMode(true);

    setTimeout(async () => {
      try {
        if (pdfRef.current) {
          const images = pdfRef.current.querySelectorAll("img");
          await Promise.all(
            Array.from(images).map((img) => {
              if (img.complete) return Promise.resolve();
              return new Promise((resolve, reject) => {
                img.onload = resolve;
                img.onerror = () => {
                  console.warn("Image non chargée, continuation...", img.src);
                  resolve();
                };
                setTimeout(resolve, 3000);
              });
            })
          );
        }

        await new Promise((resolve) => setTimeout(resolve, 500));

        const pdf = await generatePdf();

        if (pdf) {
          const pdfBlob = pdf.output("blob");
          const pdfUrl = URL.createObjectURL(pdfBlob);
          window.open(pdfUrl, "_blank");
        }

        setPreviewMode(false);
      } catch (error) {
        console.error("Erreur lors de la génération du PDF:", error);
        alert("Une erreur est survenue lors de la génération du PDF. Veuillez réessayer.");
        setPreviewMode(false);
      }
    }, 1000);
  };

  const handleDirectSave = async () => {
    if (!validateForm()) {
      controls.start({
        x: [0, -10, 10, -10, 0],
        transition: { duration: 0.5 },
      });

      const firstErrorKey = Object.keys(errors)[0];
      const firstErrorElement =
        document.querySelector(`[name="${firstErrorKey}"]`) ||
        document.querySelector(`[data-error="${firstErrorKey}"]`);
      if (firstErrorElement) {
        firstErrorElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }

      return;
    }

    const clientSig = getSignatureData(clientSignatureRef, "client");
    const trainerSig = getSignatureData(trainerSignatureRef, "trainer");
    const managerSig = getSignatureData(managerSignatureRef, "manager");

    setSignatures({
      client: clientSig,
      trainer: trainerSig,
      manager: managerSig,
    });

    setPreviewMode(true);

    setTimeout(async () => {
      try {
        if (pdfRef.current) {
          const images = pdfRef.current.querySelectorAll("img");
          await Promise.all(
            Array.from(images).map((img) => {
              if (img.complete) return Promise.resolve();
              return new Promise((resolve) => {
                img.onload = resolve;
                img.onerror = () => {
                  console.warn("Image non chargée, continuation...", img.src);
                  resolve();
                };
                setTimeout(resolve, 3000);
              });
            })
          );
        }

        await new Promise((resolve) => setTimeout(resolve, 500));

        const pdf = await generatePdf();

        if (pdf) {
          const success = await saveToSupabase(pdf);

          if (success) {
            if (isAdmin) {
              setShowClientsList(true);
            }
            console.log("✅ Document sauvegardé avec succès");
          } else {
            setPreviewMode(false);
            alert("Erreur lors de la sauvegarde du document. Veuillez réessayer.");
          }
        } else {
          setPreviewMode(false);
          alert("Erreur lors de la génération du PDF. Veuillez réessayer.");
        }
      } catch (error) {
        console.error("Erreur lors de la sauvegarde:", error);
        alert("Une erreur est survenue lors de la sauvegarde. Veuillez réessayer.");
        setPreviewMode(false);
      }
    }, 1000);
  };

  const resetForm = () => {
    setFormData({
      immatriculation: "",
      firstName: "",
      lastName: "",
      address: "",
      postalCode: "",
      city: "",
      phone: "",
      email: "",
      trainerName: "",
      technicalControlDate: "",
      termsAccepted: false,
      insuranceResponsibility: false,
    });
    setFiles({ registration: null });
    setFileUrls({ registration: null });
    setSignatures({ client: null, trainer: null, manager: null });
    setInsuranceConfirmation(false);
    if (clientSignatureRef.current) clientSignatureRef.current.clear();
    if (trainerSignatureRef.current) trainerSignatureRef.current.clear();
    if (managerSignatureRef.current) managerSignatureRef.current.clear();
    setPreviewMode(false);
    setSearchPlate("");
    setSearchError("");
    setInterventions([]);
    setNextInterventionDate("");
    setInterventionType("");
    setCurrentClientId(null);
  };

  const handleCancel = () => {
    if (previewMode) {
      setPreviewMode(false);
    } else {
      navigate(-1);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Non spécifié";
    const date = new Date(dateString);
    return date.toLocaleDateString("fr-FR");
  };

  const legalText = `Le CFA de Montargis ne peut être tenu responsable des dommages causés aux véhicules pendant les travaux pédagogiques réalisés par les apprentis sous la supervision des formateurs. Ces travaux sont effectués dans un cadre éducatif et de formation. Le propriétaire du véhicule reconnaît être informé que les interventions sont réalisées par des apprentis en formation et accepte les conditions suivantes:

1. Les délais d'intervention peuvent être plus longs que dans un garage commercial
2. Le CFA ne garantit pas les pièces et la main d'œuvre comme le ferait un garage professionnel
3. Le véhicule doit être assuré par son propriétaire pendant toute la durée des travaux
4. Le propriétaire s'engage à récupérer son véhicule dans les 7 jours suivant la notification de fin des travaux

En cas de litige, une solution à l'amiable sera recherchée en priorité.`;

  // ============ RENDERS ============
  if (isLoading) {
    return <CustomLoader />;
  }

  if (showClientsList && isAdmin) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className='min-h-screen py-12 px-4 sm:px-6 lg:px-8 bg-slate-900 text-white'>
        <div className='max-w-7xl mx-auto'>
          <div className='flex justify-between items-center mb-8'>
            <h1 className='text-3xl font-bold text-blue-400'>
              Liste des Clients Pédagogiques
            </h1>
            <button
              onClick={() => setShowClientsList(false)}
              className='px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white flex items-center'>
              <X className='w-5 h-5 mr-2' />
              Fermer
            </button>
          </div>

          <div className='bg-slate-800 rounded-lg shadow-xl p-6'>
            <div className='overflow-x-auto'>
              <table className='min-w-full divide-y divide-slate-700'>
                <thead>
                  <tr>
                    <th className='px-6 py-3 text-left text-xs font-medium text-blue-300 uppercase tracking-wider'>
                      Immatriculation
                    </th>
                    <th className='px-6 py-3 text-left text-xs font-medium text-blue-300 uppercase tracking-wider'>
                      Client
                    </th>
                    <th className='px-6 py-3 text-left text-xs font-medium text-blue-300 uppercase tracking-wider'>
                      Téléphone
                    </th>
                    <th className='px-6 py-3 text-left text-xs font-medium text-blue-300 uppercase tracking-wider'>
                      Email
                    </th>
                    <th className='px-6 py-3 text-left text-xs font-medium text-blue-300 uppercase tracking-wider'>
                      Formateur
                    </th>
                    <th className='px-6 py-3 text-left text-xs font-medium text-blue-300 uppercase tracking-wider'>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className='bg-slate-800 divide-y divide-slate-700'>
                  {allClients.length > 0 ? (
                    allClients.map((client, index) => (
                      <tr
                        key={client.id}
                        className={index % 2 === 0 ? "bg-slate-800" : "bg-slate-750"}>
                        <td className='px-6 py-4 whitespace-nowrap text-sm font-medium text-white'>
                          {client.immatriculation}
                        </td>
                        <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-300'>
                          {client.last_name} {client.first_name}
                        </td>
                        <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-300'>
                          {client.phone || "Non renseigné"}
                        </td>
                        <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-300'>
                          {client.email || "Non renseigné"}
                        </td>
                        <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-300'>
                          {client.trainer_name || "Non assigné"}
                        </td>
                        <td className='px-6 py-4 whitespace-nowrap text-sm font-medium'>
                          <div className='flex items-center gap-2'>
                            <button
                              onClick={() => loadClientById(client.id)}
                              className='text-blue-400 hover:text-blue-300 px-2 py-1 rounded hover:bg-blue-400/10 transition-colors'>
                              Modifier
                            </button>
                            <Link
                              to='/pole-auto/intervention'
                              state={{ client: client }}
                              className='text-green-400 hover:text-green-300 px-2 py-1 rounded hover:bg-green-400/10 transition-colors'>
                              Intervention
                            </Link>
                            {client.contract_file && (
                              <button
                                onClick={async () => {
                                  const { data } = await supabase.storage
                                    .from("client-documents")
                                    .createSignedUrl(client.contract_file, 3600);
                                  if (data?.signedUrl) {
                                    window.open(data.signedUrl, "_blank");
                                  }
                                }}
                                className='text-purple-400 hover:text-purple-300 px-2 py-1 rounded hover:bg-purple-400/10 transition-colors'>
                                Contrat
                              </button>
                            )}
                            <button
                              onClick={() =>
                                handleDeleteClient(
                                  client.id,
                                  `${client.last_name} ${client.first_name}`
                                )
                              }
                              className='text-red-400 hover:text-red-300 px-2 py-1 rounded hover:bg-red-400/10 transition-colors flex items-center gap-1'
                              title='Supprimer ce client'>
                              <Trash2 className='w-4 h-4' />
                              Supprimer
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan='6' className='px-6 py-4 text-center text-gray-400'>
                        Aucun client trouvé
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className='mt-6 flex justify-center gap-4'>
              <button
                onClick={() => {
                  resetForm();
                  setShowClientsList(false);
                }}
                className='px-6 py-3 bg-blue-600 hover:bg-blue-700 rounded-lg text-white flex items-center'>
                <PlusCircle className='w-5 h-5 mr-2' />
                Nouveau Client
              </button>

              <Link
                to='/pole-auto/intervention'
                className='flex items-center text-white bg-green-600 hover:bg-green-700 px-4 py-2 rounded-lg transition-all transform hover:scale-105 active:scale-95'>
                <Wrench className='w-4 h-4 mr-2' />
                Nouvelle Intervention
              </Link>
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className='min-h-screen py-12 px-4 sm:px-6 lg:px-8 bg-slate-900 text-white overflow-hidden'>
      <div className='fixed inset-0 z-0'></div>

      {/* Modal Caméra */}
      <AnimatePresence>
        {showCamera && (
          <CameraModal
            onCapture={handleCameraCapture}
            onClose={() => setShowCamera(false)}
          />
        )}
      </AnimatePresence>

      <motion.div
        animate={logoControls}
        className='fixed top-40 right-6 w-24 h-24 md:w-36 md:h-36 z-10'>
        <img
          src='/images/9b6b737e-c7d2-40e5-8a92-8bbe017d0fa9.png'
          alt='Logiciel Garage CFA'
          className='w-full h-full object-contain'
        />
      </motion.div>

      <div className='max-w-5xl mx-auto relative z-10'>
        <motion.div
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}>
          <h1 className='text-3xl md:text-4xl font-bold mb-2 text-center bg-clip-text text-transparent bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500'>
            Nouveau Client Pédagogique
          </h1>
          <p className='text-gray-300 text-center mb-10'>
            Enregistrez les informations du nouveau client pédagogique et de son véhicule
          </p>
        </motion.div>

        {missingBuckets.length > 0 && (
          <div className='bg-yellow-500/20 border border-yellow-500 rounded-lg p-4 mb-6'>
            <h3 className='text-yellow-400 font-semibold mb-2 flex items-center'>
              <AlertTriangle className='w-5 h-5 mr-2' />
              Attention : Configuration Supabase incomplète
            </h3>
            <p className='text-yellow-300'>
              Les buckets de stockage suivants sont nécessaires mais n'existent pas dans votre projet Supabase :
            </p>
            <ul className='list-disc ml-6 mt-2'>
              {missingBuckets.map((bucket) => (
                <li key={bucket} className='text-yellow-300'>
                  {bucket}
                </li>
              ))}
            </ul>
            <p className='mt-2 text-yellow-300'>
              Veuillez créer ces buckets manuellement dans le dashboard Supabase (section Storage) et définir leurs politiques pour permettre le stockage des fichiers.
            </p>
          </div>
        )}

        {!previewMode && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className='bg-slate-800/80 backdrop-blur-sm rounded-xl shadow-xl p-6 mb-8'>
            <div className='flex justify-between items-center mb-4'>
              <div className='flex items-center'>
                <img
                  src='/images/9b6b737e-c7d2-40e5-8a92-8bbe017d0fa9.png'
                  alt='Logo CFA'
                  className='w-8 h-8 mr-2'
                />
                <h2 className='text-xl font-semibold text-blue-400'>
                  Rechercher un client existant
                </h2>
              </div>

              <div className='flex gap-2'>
                {isAdmin && (
                  <button
                    onClick={() => setShowClientsList(true)}
                    className='flex items-center text-white bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg'>
                    <List className='w-4 h-4 mr-2' />
                    Liste des clients
                  </button>
                )}

                <Link
                  to='/pole-auto/intervention'
                  className='flex items-center text-white bg-green-600 hover:bg-green-700 px-4 py-2 rounded-lg'>
                  <Wrench className='w-4 h-4 mr-2' />
                  Nouvelle Intervention
                </Link>
              </div>
            </div>

            <form onSubmit={searchClientByPlate} className='flex flex-col md:flex-row gap-4'>
              <div className='flex-grow'>
                <div className='relative'>
                  <Car className='absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400' />
                  <input
                    type='text'
                    value={searchPlate}
                    onChange={(e) => setSearchPlate(e.target.value.toUpperCase())}
                    placeholder='Recherche par immatriculation'
                    className='w-full bg-slate-700 border border-slate-600 rounded-lg py-3 pl-10 pr-4 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500'
                  />
                </div>
                {searchError && <p className='mt-2 text-sm text-red-400'>{searchError}</p>}
              </div>
              <button
                type='submit'
                disabled={isSearching}
                className='bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition-colors flex items-center justify-center'>
                {isSearching ? (
                  <>
                    <LoaderIcon className='animate-spin mr-2 h-5 w-5' />
                    Recherche...
                  </>
                ) : (
                  <>
                    <Search className='mr-2 h-5 w-5' />
                    Rechercher
                  </>
                )}
              </button>
            </form>
          </motion.div>
        )}

        {/* PDF CACHÉ */}
        <div
          style={{
            position: "absolute",
            left: "-9999px",
            top: "-9999px",
            width: "210mm",
          }}>
          <div
            ref={pdfRef}
            style={{
              width: "210mm",
              backgroundColor: "#ffffff",
              color: "#000000",
              padding: "20mm",
              fontFamily: "Arial, sans-serif",
            }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                marginBottom: "40px",
              }}>
              <div>
                <h2
                  style={{
                    fontSize: "28px",
                    fontWeight: "bold",
                    color: "#1e3a8a",
                    marginBottom: "8px",
                    margin: "0",
                  }}>
                  Fiche Client Pédagogique
                </h2>
                <p style={{ color: "#4b5563", margin: "8px 0" }}>
                  CFA de Montargis - Pôle Automobile
                </p>
              </div>
              <img
                src='/images/9b6b737e-c7d2-40e5-8a92-8bbe017d0fa9.png'
                alt='Logo CFA Garage'
                style={{
                  width: "100px",
                  height: "100px",
                  objectFit: "contain",
                }}
              />
            </div>

            <div
              style={{
                borderTop: "2px solid #d1d5db",
                borderBottom: "2px solid #d1d5db",
                padding: "20px 0",
                margin: "20px 0",
              }}>
              <h3
                style={{
                  fontSize: "22px",
                  fontWeight: "600",
                  marginBottom: "16px",
                  color: "#1e40af",
                  margin: "0 0 16px 0",
                }}>
                Informations du véhicule
              </h3>
              <p style={{ marginBottom: "10px", fontSize: "16px" }}>
                <strong>Immatriculation:</strong> {formData.immatriculation}
              </p>
              <p style={{ marginBottom: "10px", fontSize: "16px" }}>
                <strong>Date de contrôle technique:</strong> {formatDate(formData.technicalControlDate)}
              </p>
              <p style={{ marginBottom: "10px", fontSize: "16px" }}>
                <strong>Attestation d'assurance:</strong>{" "}
                {formData.insuranceResponsibility ? "✓ Fournie" : "✗ Non fournie"}
              </p>

              {nextInterventionDate && (
                <div
                  style={{
                    marginTop: "12px",
                    padding: "12px",
                    backgroundColor: "#eff6ff",
                    border: "1px solid #bfdbfe",
                    borderRadius: "6px",
                  }}>
                  <p style={{ marginBottom: "6px", fontSize: "16px" }}>
                    <strong>Prochaine intervention:</strong> {formatDate(nextInterventionDate)}
                  </p>
                  <p style={{ margin: "0", fontSize: "16px" }}>
                    <strong>Type:</strong> {interventionType || "Non spécifié"}
                  </p>
                </div>
              )}

              {fileUrls.registration && (
                <div style={{ marginTop: "20px" }}>
                  <h4
                    style={{
                      fontSize: "18px",
                      fontWeight: "600",
                      marginBottom: "12px",
                      color: "#1e40af",
                      margin: "0 0 12px 0",
                    }}>
                    Carte grise du véhicule
                  </h4>
                  <div
                    style={{
                      textAlign: "center",
                      padding: "10px",
                      backgroundColor: "#f9fafb",
                      border: "1px solid #e5e7eb",
                      borderRadius: "6px",
                    }}>
                    {fileUrls.registration.startsWith("data:image") ||
                    fileUrls.registration.includes("/storage/") ||
                    fileUrls.registration.startsWith("blob:") ||
                    /\.(jpg|jpeg|png|gif|webp)/.test(fileUrls.registration) ? (
                      <img
                        src={fileUrls.registration}
                        alt='Carte grise'
                        style={{
                          maxWidth: "100%",
                          maxHeight: "400px",
                          objectFit: "contain",
                          margin: "0 auto",
                          display: "block",
                          border: "1px solid #d1d5db",
                          borderRadius: "4px",
                        }}
                        crossOrigin='anonymous'
                      />
                    ) : (
                      <p
                        style={{
                          color: "#6b7280",
                          margin: "0",
                          padding: "20px",
                          fontSize: "14px",
                        }}>
                        📄 Document PDF joint - Voir le fichier original dans les documents
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div
              style={{
                borderBottom: "2px solid #d1d5db",
                padding: "20px 0",
                marginBottom: "20px",
              }}>
              <h3
                style={{
                  fontSize: "22px",
                  fontWeight: "600",
                  marginBottom: "16px",
                  color: "#1e40af",
                  margin: "0 0 16px 0",
                }}>
                Informations du client
              </h3>
              <p style={{ marginBottom: "10px", fontSize: "16px" }}>
                <strong>Nom:</strong> {formData.lastName}
              </p>
              <p style={{ marginBottom: "10px", fontSize: "16px" }}>
                <strong>Prénom:</strong> {formData.firstName}
              </p>
              <p style={{ marginBottom: "10px", fontSize: "16px" }}>
                <strong>Adresse:</strong> {formData.address}
              </p>
              <p style={{ marginBottom: "10px", fontSize: "16px" }}>
                <strong>Code postal:</strong> {formData.postalCode}
              </p>
              <p style={{ marginBottom: "10px", fontSize: "16px" }}>
                <strong>Ville:</strong> {formData.city}
              </p>
              <p style={{ marginBottom: "10px", fontSize: "16px" }}>
                <strong>Téléphone:</strong> {formData.phone}
              </p>
              <p style={{ marginBottom: "10px", fontSize: "16px" }}>
                <strong>Email:</strong> {formData.email}
              </p>
            </div>

            <div
              style={{
                borderBottom: "2px solid #d1d5db",
                padding: "20px 0",
                marginBottom: "20px",
              }}>
              <h3
                style={{
                  fontSize: "22px",
                  fontWeight: "600",
                  marginBottom: "16px",
                  color: "#1e40af",
                  margin: "0 0 16px 0",
                }}>
                Documents fournis
              </h3>
              <p style={{ marginBottom: "10px", fontSize: "16px" }}>
                <strong>Assurance:</strong>{" "}
                {formData.insuranceResponsibility
                  ? "✓ Client a confirmé être assuré"
                  : "✗ Non confirmée"}
              </p>
              <p style={{ marginBottom: "10px", fontSize: "16px" }}>
                <strong>Carte grise:</strong>{" "}
                {files.registration || fileUrls.registration ? "✓ Fournie" : "✗ Non fournie"}
              </p>
            </div>

            <div
              style={{
                borderBottom: "2px solid #d1d5db",
                padding: "20px 0",
                marginBottom: "20px",
              }}>
              <h3
                style={{
                  fontSize: "22px",
                  fontWeight: "600",
                  marginBottom: "16px",
                  color: "#1e40af",
                  margin: "0 0 16px 0",
                }}>
                Formateur responsable
              </h3>
              <p style={{ margin: "0", fontSize: "16px" }}>
                <strong>Formateur:</strong> {formData.trainerName || "Non spécifié"}
              </p>
            </div>

            <div
              style={{
                fontSize: "13px",
                color: "#374151",
                border: "1px solid #d1d5db",
                padding: "16px",
                backgroundColor: "#f9fafb",
                borderRadius: "6px",
                marginBottom: "20px",
                lineHeight: "1.6",
              }}>
              <h3
                style={{
                  fontSize: "16px",
                  fontWeight: "600",
                  marginBottom: "12px",
                  color: "#1f2937",
                  margin: "0 0 12px 0",
                }}>
                Mentions légales:
              </h3>
              <p style={{ whiteSpace: "pre-line", margin: "0" }}>{legalText}</p>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr",
                gap: "30px",
                marginTop: "40px",
              }}>
              <div style={{ textAlign: "center" }}>
                <p style={{ fontWeight: "600", marginBottom: "12px", fontSize: "16px" }}>
                  Signature du client
                </p>
                <div
                  style={{
                    border: "2px solid #d1d5db",
                    padding: "10px",
                    minHeight: "150px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "#ffffff",
                    borderRadius: "4px",
                  }}>
                  {signatures.client && signatures.client.trim() !== "" ? (
                    <img
                      src={signatures.client}
                      alt='Signature client'
                      style={{
                        maxHeight: "130px",
                        maxWidth: "100%",
                        width: "auto",
                        height: "auto",
                        display: "block",
                      }}
                    />
                  ) : (
                    <p style={{ color: "#ef4444", fontSize: "14px", margin: "0" }}>
                      Signature manquante
                    </p>
                  )}
                </div>
              </div>

              <div style={{ textAlign: "center" }}>
                <p style={{ fontWeight: "600", marginBottom: "12px", fontSize: "16px" }}>
                  Signature du formateur
                </p>
                <div
                  style={{
                    border: "2px solid #d1d5db",
                    padding: "10px",
                    minHeight: "150px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "#ffffff",
                    borderRadius: "4px",
                  }}>
                  {signatures.trainer && signatures.trainer.trim() !== "" ? (
                    <img
                      src={signatures.trainer}
                      alt='Signature formateur'
                      style={{
                        maxHeight: "130px",
                        maxWidth: "100%",
                        width: "auto",
                        height: "auto",
                        display: "block",
                      }}
                    />
                  ) : (
                    <p style={{ color: "#ef4444", fontSize: "14px", margin: "0" }}>
                      Signature manquante
                    </p>
                  )}
                </div>
              </div>

              <div style={{ textAlign: "center" }}>
                <p style={{ fontWeight: "600", marginBottom: "12px", fontSize: "16px" }}>
                  Signature du responsable
                </p>
                <div
                  style={{
                    border: "2px solid #d1d5db",
                    padding: "10px",
                    minHeight: "150px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "#ffffff",
                    borderRadius: "4px",
                  }}>
                  {signatures.manager && signatures.manager.trim() !== "" ? (
                    <img
                      src={signatures.manager}
                      alt='Signature responsable'
                      style={{
                        maxHeight: "130px",
                        maxWidth: "100%",
                        width: "auto",
                        height: "auto",
                        display: "block",
                      }}
                    />
                  ) : (
                    <p style={{ color: "#ef4444", fontSize: "14px", margin: "0" }}>
                      Signature manquante
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div
              style={{
                textAlign: "right",
                marginTop: "40px",
                borderTop: "1px solid #d1d5db",
                paddingTop: "20px",
              }}>
              <p style={{ fontSize: "15px", color: "#4b5563", margin: "0" }}>
                Date d'établissement: {new Date().toLocaleDateString("fr-FR")}
              </p>
            </div>
          </div>
        </div>

        {/* FORMULAIRE */}
        {!previewMode && (
          <motion.form
            ref={formRef}
            onSubmit={handleSubmit}
            animate={controls}
            className='bg-slate-800/90 backdrop-blur-lg rounded-xl shadow-xl p-6 md:p-8'>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6'>
              {/* Immatriculation */}
              <motion.div whileHover={{ scale: 1.02 }} className='col-span-1'>
                <label className='text-sm font-medium text-blue-300 mb-2 block'>
                  Immatriculation du véhicule *
                </label>
                <div className='relative'>
                  <Car className='absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400' />
                  <input
                    type='text'
                    name='immatriculation'
                    value={formData.immatriculation}
                    onChange={handleChange}
                    className={`w-full bg-slate-700 border ${
                      errors.immatriculation ? "border-red-500" : "border-slate-600"
                    } rounded-lg py-3 pl-10 pr-4 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    placeholder='AB-123-CD'
                  />
                </div>
                {errors.immatriculation && (
                  <p className='mt-1 text-sm text-red-400'>{errors.immatriculation}</p>
                )}
              </motion.div>

              {/* Date contrôle technique */}
              <motion.div whileHover={{ scale: 1.02 }} className='col-span-1'>
                <label className='text-sm font-medium text-blue-300 mb-2 block'>
                  Date de contrôle technique
                </label>
                <div className='relative'>
                  <Calendar className='absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400' />
                  <input
                    type='date'
                    name='technicalControlDate'
                    value={formData.technicalControlDate}
                    onChange={handleChange}
                    className='w-full bg-slate-700 border border-slate-600 rounded-lg py-3 pl-10 pr-4 text-white focus:outline-none focus:ring-2 focus:ring-blue-500'
                  />
                </div>
              </motion.div>

              {/* Prénom */}
              <motion.div whileHover={{ scale: 1.02 }} className='col-span-1'>
                <label className='text-sm font-medium text-blue-300 mb-2 block'>
                  Prénom du client *
                </label>
                <div className='relative'>
                  <User className='absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400' />
                  <input
                    type='text'
                    name='firstName'
                    value={formData.firstName}
                    onChange={handleChange}
                    className={`w-full bg-slate-700 border ${
                      errors.firstName ? "border-red-500" : "border-slate-600"
                    } rounded-lg py-3 pl-10 pr-4 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    placeholder='Jean'
                  />
                </div>
                {errors.firstName && (
                  <p className='mt-1 text-sm text-red-400'>{errors.firstName}</p>
                )}
              </motion.div>

              {/* Nom */}
              <motion.div whileHover={{ scale: 1.02 }} className='col-span-1'>
                <label className='text-sm font-medium text-blue-300 mb-2 block'>
                  Nom du client *
                </label>
                <div className='relative'>
                  <User className='absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400' />
                  <input
                    type='text'
                    name='lastName'
                    value={formData.lastName}
                    onChange={handleChange}
                    className={`w-full bg-slate-700 border ${
                      errors.lastName ? "border-red-500" : "border-slate-600"
                    } rounded-lg py-3 pl-10 pr-4 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    placeholder='DUPONT'
                  />
                </div>
                {errors.lastName && (
                  <p className='mt-1 text-sm text-red-400'>{errors.lastName}</p>
                )}
              </motion.div>

              {/* Adresse */}
              <motion.div whileHover={{ scale: 1.02 }} className='col-span-1 md:col-span-2'>
                <label className='text-sm font-medium text-blue-300 mb-2 block'>Adresse *</label>
                <div className='relative'>
                  <MapPin className='absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400' />
                  <input
                    type='text'
                    name='address'
                    value={formData.address}
                    onChange={handleChange}
                    className={`w-full bg-slate-700 border ${
                      errors.address ? "border-red-500" : "border-slate-600"
                    } rounded-lg py-3 pl-10 pr-4 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    placeholder='12 Rue de la République'
                  />
                </div>
                {errors.address && <p className='mt-1 text-sm text-red-400'>{errors.address}</p>}
              </motion.div>

              {/* Code postal */}
              <motion.div whileHover={{ scale: 1.02 }} className='col-span-1'>
                <label className='text-sm font-medium text-blue-300 mb-2 block'>
                  Code postal *
                </label>
                <input
                  type='text'
                  name='postalCode'
                  value={formData.postalCode}
                  onChange={handleChange}
                  className={`w-full bg-slate-700 border ${
                    errors.postalCode ? "border-red-500" : "border-slate-600"
                  } rounded-lg py-3 px-4 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  placeholder='45200'
                  maxLength='5'
                />
                {errors.postalCode && (
                  <p className='mt-1 text-sm text-red-400'>{errors.postalCode}</p>
                )}
              </motion.div>

              {/* Ville */}
              <motion.div whileHover={{ scale: 1.02 }} className='col-span-1'>
                <label className='text-sm font-medium text-blue-300 mb-2 block'>Ville *</label>
                <input
                  type='text'
                  name='city'
                  value={formData.city}
                  onChange={handleChange}
                  className={`w-full bg-slate-700 border ${
                    errors.city ? "border-red-500" : "border-slate-600"
                  } rounded-lg py-3 px-4 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  placeholder='Montargis'
                />
                {errors.city && <p className='mt-1 text-sm text-red-400'>{errors.city}</p>}
              </motion.div>

              {/* Téléphone */}
              <motion.div whileHover={{ scale: 1.02 }} className='col-span-1'>
                <label className='text-sm font-medium text-blue-300 mb-2 block'>Téléphone *</label>
                <div className='relative'>
                  <Phone className='absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400' />
                  <input
                    type='tel'
                    name='phone'
                    value={formData.phone}
                    onChange={handleChange}
                    className={`w-full bg-slate-700 border ${
                      errors.phone ? "border-red-500" : "border-slate-600"
                    } rounded-lg py-3 pl-10 pr-4 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    placeholder='06 12 34 56 78'
                  />
                </div>
                {errors.phone && <p className='mt-1 text-sm text-red-400'>{errors.phone}</p>}
              </motion.div>

              {/* Email */}
              <motion.div whileHover={{ scale: 1.02 }} className='col-span-1'>
                <label className='text-sm font-medium text-blue-300 mb-2 block'>Email *</label>
                <div className='relative'>
                  <Mail className='absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400' />
                  <input
                    type='email'
                    name='email'
                    value={formData.email}
                    onChange={handleChange}
                    className={`w-full bg-slate-700 border ${
                      errors.email ? "border-red-500" : "border-slate-600"
                    } rounded-lg py-3 pl-10 pr-4 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    placeholder='exemple@email.com'
                  />
                </div>
                {errors.email && <p className='mt-1 text-sm text-red-400'>{errors.email}</p>}
              </motion.div>

              {/* Formateur */}
              <motion.div whileHover={{ scale: 1.02 }} className='col-span-1 md:col-span-2'>
                <label className='text-sm font-medium text-blue-300 mb-2 block'>
                  Formateur responsable *
                </label>
                <select
                  name='trainerName'
                  value={formData.trainerName}
                  onChange={handleChange}
                  className={`w-full bg-slate-700 border ${
                    errors.trainerName ? "border-red-500" : "border-slate-600"
                  } rounded-lg py-3 px-4 text-white focus:outline-none focus:ring-2 focus:ring-blue-500`}>
                  <option value=''>Sélectionnez un formateur</option>
                  {trainers.map((trainer) => (
                    <option key={trainer.id} value={trainer.name}>
                      {trainer.name}
                    </option>
                  ))}
                </select>
                {errors.trainerName && (
                  <p className='mt-1 text-sm text-red-400'>{errors.trainerName}</p>
                )}
              </motion.div>

              {/* ============ NOUVELLE ZONE CARTE GRISE AVEC DRAG & DROP ============ */}
              <motion.div whileHover={{ scale: 1.02 }} className='col-span-1 md:col-span-2'>
                <label className='text-sm font-medium text-blue-300 mb-2 block'>
                  Carte grise du véhicule *
                </label>

                {!fileUrls.registration ? (
                  <FileDropZone
                    onFileSelect={handleFileSelect}
                    accept='image/*,application/pdf'
                    className='border-2 border-dashed border-slate-600 rounded-lg p-8 hover:border-blue-500 transition-colors'>
                    <div className='text-center'>
                      <Upload className='w-12 h-12 text-slate-400 mx-auto mb-4' />
                      <p className='text-white font-medium mb-2'>
                        Glissez-déposez votre fichier ici
                      </p>
                      <p className='text-gray-400 text-sm mb-4'>ou</p>
                      <div className='flex flex-wrap justify-center gap-3'>
                        <span className='px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2 cursor-pointer'>
                          <FileText className='w-5 h-5' />
                          Choisir un fichier
                        </span>
                        <button
                          type='button'
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowCamera(true);
                          }}
                          className='px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center gap-2'>
                          <Camera className='w-5 h-5' />
                          Prendre une photo
                        </button>
                      </div>
                      <p className='text-gray-500 text-xs mt-4'>
                        Formats acceptés : JPG, PNG, PDF
                      </p>
                    </div>
                  </FileDropZone>
                ) : (
                  <div className='bg-slate-700 rounded-lg p-4'>
                    <div className='flex items-center justify-between mb-3'>
                      <p className='text-green-400 flex items-center gap-2'>
                        <CheckCircle className='w-5 h-5' />
                        Fichier chargé avec succès
                      </p>
                      <button
                        type='button'
                        onClick={() => {
                          setFileUrls({ ...fileUrls, registration: null });
                          setFiles({ ...files, registration: null });
                        }}
                        className='p-2 bg-red-500 hover:bg-red-600 text-white rounded-full transition-colors'>
                        <X className='w-4 h-4' />
                      </button>
                    </div>

                    {fileUrls.registration.startsWith("data:image") ||
                    fileUrls.registration.startsWith("blob:") ||
                    fileUrls.registration.includes("/storage/") ||
                    /\.(jpg|jpeg|png|gif|webp)$/i.test(fileUrls.registration) ? (
                      <div className='flex justify-center'>
                        <img
                          src={fileUrls.registration}
                          alt='Carte grise'
                          className='max-w-full max-h-72 object-contain rounded-lg border border-slate-600'
                        />
                      </div>
                    ) : (
                      <div className='flex items-center justify-center p-6 bg-slate-600 rounded-lg'>
                        <FileText className='w-8 h-8 text-blue-400 mr-3' />
                        <span className='text-white'>Document PDF chargé</span>
                      </div>
                    )}

                    <div className='flex justify-center gap-3 mt-4'>
                      <FileDropZone
                        onFileSelect={handleFileSelect}
                        accept='image/*,application/pdf'
                        className='inline-block'>
                        <span className='px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2 cursor-pointer'>
                          <Upload className='w-4 h-4' />
                          Remplacer
                        </span>
                      </FileDropZone>
                      <button
                        type='button'
                        onClick={() => setShowCamera(true)}
                        className='px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center gap-2'>
                        <Camera className='w-4 h-4' />
                        Nouvelle photo
                      </button>
                    </div>
                  </div>
                )}

                {errors.registration && (
                  <p className='mt-2 text-sm text-red-400'>{errors.registration}</p>
                )}
              </motion.div>

              {/* Checkbox assurance */}
              <motion.div whileHover={{ scale: 1.02 }} className='col-span-1 md:col-span-2'>
                <div className='flex items-start'>
                  <input
                    type='checkbox'
                    name='insuranceResponsibility'
                    checked={formData.insuranceResponsibility}
                    onChange={handleChange}
                    className='mt-1 h-5 w-5 rounded border-slate-600 bg-slate-700 text-blue-600 focus:ring-2 focus:ring-blue-500'
                  />
                  <label className='ml-3 text-sm text-gray-300'>
                    <span className='font-medium text-blue-300'>Attestation d'assurance *</span>
                    <p className='mt-1'>
                      Je confirme que le véhicule est couvert par une assurance valide pendant
                      toute la durée des travaux pédagogiques.
                    </p>
                  </label>
                </div>
                {errors.insuranceResponsibility && (
                  <p className='mt-1 text-sm text-red-400'>{errors.insuranceResponsibility}</p>
                )}
              </motion.div>

              {/* SIGNATURES */}
              <motion.div whileHover={{ scale: 1.02 }} className='col-span-1 md:col-span-2'>
                <h3 className='text-lg font-semibold text-blue-300 mb-4 flex items-center'>
                  <FileText className='mr-2' />
                  Signatures
                </h3>

                <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
                  {/* Signature client */}
                  <div>
                    <label className='text-sm font-medium text-blue-300 mb-2 block'>
                      Signature du client *
                    </label>
                    <div className='border-2 border-slate-600 rounded-lg overflow-hidden bg-white'>
                      <SignatureCanvas
                        ref={clientSignatureRef}
                        canvasProps={{
                          width: canvasSize.width,
                          height: canvasSize.height,
                          className: "signature-canvas",
                        }}
                        onEnd={() => {
                          const data = clientSignatureRef.current.toDataURL();
                          setSignatures((prev) => ({ ...prev, client: data }));
                        }}
                      />
                    </div>

                    {signatures.client && (
                      <div className='mt-2 p-2 bg-slate-600/50 rounded border border-slate-500'>
                        <p className='text-xs text-green-400 mb-1 flex items-center'>
                          <CheckCircle className='w-3 h-3 mr-1' />
                          Signature enregistrée
                        </p>
                        <img
                          src={signatures.client}
                          alt='Aperçu signature client'
                          className='max-h-16 w-full object-contain border border-slate-400 bg-white rounded'
                        />
                      </div>
                    )}

                    <button
                      type='button'
                      onClick={() => clearSignature(clientSignatureRef, "client")}
                      className='mt-2 text-sm text-red-400 hover:text-red-300 flex items-center'>
                      <X className='w-4 h-4 mr-1' />
                      Effacer
                    </button>
                    {errors.clientSignature && (
                      <p className='mt-1 text-sm text-red-400'>{errors.clientSignature}</p>
                    )}
                  </div>

                  {/* Signature formateur */}
                  <div>
                    <label className='text-sm font-medium text-blue-300 mb-2 block'>
                      Signature du formateur *
                    </label>
                    <div className='border-2 border-slate-600 rounded-lg overflow-hidden bg-white'>
                      <SignatureCanvas
                        ref={trainerSignatureRef}
                        canvasProps={{
                          width: canvasSize.width,
                          height: canvasSize.height,
                          className: "signature-canvas",
                        }}
                        onEnd={() => {
                          const data = trainerSignatureRef.current.toDataURL();
                          setSignatures((prev) => ({ ...prev, trainer: data }));
                        }}
                      />
                    </div>

                    {signatures.trainer && (
                      <div className='mt-2 p-2 bg-slate-600/50 rounded border border-slate-500'>
                        <p className='text-xs text-green-400 mb-1 flex items-center'>
                          <CheckCircle className='w-3 h-3 mr-1' />
                          Signature enregistrée
                        </p>
                        <img
                          src={signatures.trainer}
                          alt='Aperçu signature formateur'
                          className='max-h-16 w-full object-contain border border-slate-400 bg-white rounded'
                        />
                      </div>
                    )}

                    <button
                      type='button'
                      onClick={() => clearSignature(trainerSignatureRef, "trainer")}
                      className='mt-2 text-sm text-red-400 hover:text-red-300 flex items-center'>
                      <X className='w-4 h-4 mr-1' />
                      Effacer
                    </button>
                    {errors.trainerSignature && (
                      <p className='mt-1 text-sm text-red-400'>{errors.trainerSignature}</p>
                    )}
                  </div>

                  {/* Signature responsable */}
                  <div>
                    <label className='text-sm font-medium text-blue-300 mb-2 block'>
                      Signature du responsable *
                    </label>
                    <div className='border-2 border-slate-600 rounded-lg overflow-hidden bg-white'>
                      <SignatureCanvas
                        ref={managerSignatureRef}
                        canvasProps={{
                          width: canvasSize.width,
                          height: canvasSize.height,
                          className: "signature-canvas",
                        }}
                        onEnd={() => {
                          const data = managerSignatureRef.current.toDataURL();
                          setSignatures((prev) => ({ ...prev, manager: data }));
                        }}
                      />
                    </div>

                    {signatures.manager && (
                      <div className='mt-2 p-2 bg-slate-600/50 rounded border border-slate-500'>
                        <p className='text-xs text-green-400 mb-1 flex items-center'>
                          <CheckCircle className='w-3 h-3 mr-1' />
                          Signature enregistrée
                        </p>
                        <img
                          src={signatures.manager}
                          alt='Aperçu signature responsable'
                          className='max-h-16 w-full object-contain border border-slate-400 bg-white rounded'
                        />
                      </div>
                    )}

                    <button
                      type='button'
                      onClick={() => clearSignature(managerSignatureRef, "manager")}
                      className='mt-2 text-sm text-red-400 hover:text-red-300 flex items-center'>
                      <X className='w-4 h-4 mr-1' />
                      Effacer
                    </button>
                    {errors.managerSignature && (
                      <p className='mt-1 text-sm text-red-400'>{errors.managerSignature}</p>
                    )}
                  </div>
                </div>
              </motion.div>

              {/* Mentions légales */}
              <motion.div whileHover={{ scale: 1.02 }} className='col-span-1 md:col-span-2'>
                <div className='bg-slate-700/50 rounded-lg p-4 mb-4'>
                  <h4 className='text-sm font-semibold text-blue-300 mb-2 flex items-center'>
                    <ShieldCheck className='mr-2 h-5 w-5' />
                    Mentions légales
                  </h4>
                  <p className='text-xs text-gray-300 whitespace-pre-line'>{legalText}</p>
                </div>
                <div className='flex items-start'>
                  <input
                    type='checkbox'
                    name='termsAccepted'
                    checked={formData.termsAccepted}
                    onChange={handleChange}
                    className='mt-1 h-5 w-5 rounded border-slate-600 bg-slate-700 text-blue-600 focus:ring-2 focus:ring-blue-500'
                  />
                  <label className='ml-3 text-sm text-gray-300'>
                    J'ai lu et j'accepte les mentions légales ci-dessus *
                  </label>
                </div>
                {errors.termsAccepted && (
                  <p className='mt-1 text-sm text-red-400'>{errors.termsAccepted}</p>
                )}
              </motion.div>
            </div>

            {errors.buckets && (
              <div className='mt-6 p-4 bg-yellow-500/20 border border-yellow-500 rounded-lg'>
                <p className='text-yellow-400'>{errors.buckets}</p>
              </div>
            )}

            {errors.submit && (
              <div className='mt-6 p-4 bg-red-500/20 border border-red-500 rounded-lg'>
                <p className='text-red-400'>{errors.submit}</p>
              </div>
            )}
          </motion.form>
        )}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className='flex flex-col sm:flex-row justify-center gap-4 mt-8'>
          <motion.button
            type='button'
            onClick={handleCancel}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className='px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors flex items-center justify-center'>
            <X className='w-5 h-5 mr-2' />
            <span>Annuler</span>
          </motion.button>

          {!previewMode && isAdmin && (
            <motion.button
              type='button'
              onClick={() => setShowClientsList(true)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className='px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors flex items-center justify-center'>
              <List className='w-5 h-5 mr-2' />
              <span>Liste clients</span>
            </motion.button>
          )}

          {!previewMode && (
            <>
              <motion.button
                type='button'
                onClick={handleSubmit}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className='px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center justify-center'>
                <Eye className='w-5 h-5 mr-2' />
                <span>Prévisualiser PDF</span>
              </motion.button>

              <motion.button
                type='button'
                onClick={handleDirectSave}
                disabled={saving}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className={`px-6 py-3 ${
                  saving || success
                    ? "bg-green-600"
                    : "bg-gradient-to-r from-green-600 to-green-800 hover:from-green-700 hover:to-green-900"
                } text-white rounded-lg transition-colors flex items-center justify-center`}>
                {saving ? (
                  <>
                    <svg
                      className='animate-spin -ml-1 mr-3 h-5 w-5 text-white'
                      xmlns='http://www.w3.org/2000/svg'
                      fill='none'
                      viewBox='0 0 24 24'>
                      <circle
                        className='opacity-25'
                        cx='12'
                        cy='12'
                        r='10'
                        stroke='currentColor'
                        strokeWidth='4'></circle>
                      <path
                        className='opacity-75'
                        fill='currentColor'
                        d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'></path>
                    </svg>
                    <span>Enregistrement...</span>
                  </>
                ) : success ? (
                  <>
                    <CheckCircle className='w-5 h-5 mr-2' />
                    <span>Enregistré avec succès!</span>
                  </>
                ) : (
                  <>
                    <Save className='w-5 h-5 mr-2' />
                    <span>Enregistrer</span>
                  </>
                )}
              </motion.button>
            </>
          )}
        </motion.div>
      </div>
    </motion.div>
  );
};

export default ClientForm;
